import { useEffect, useRef } from 'react';

import { useRiveFile } from '@rive-app/react-canvas';
import { observer } from 'mobx-react';

import { useStores } from '../../stores/useStores';

import './initRiveRuntime';
import { logRiveLoad, riveLoadInstrumentation } from './riveLoadInstrumentation';

const RiveFilePreloader: React.FC<{ url: string }> = observer(({ url }) => {
  const { riveFileStore } = useStores();
  const file = useRiveFile({ src: url });
  const startedAtRef = useRef<number>(0);
  const mountInfoRef = useRef<{ active: number; mountIndex: number } | null>(null);
  const loggedRef = useRef<boolean>(false);

  useEffect(() => {
    startedAtRef.current = performance.now();
    loggedRef.current = false;
    mountInfoRef.current = riveLoadInstrumentation.incActive();
    return () => {
      riveLoadInstrumentation.decActive();
    };
  }, [url]);

  useEffect(() => {
    if (!file || loggedRef.current) return;
    const { status, riveFile } = file;

    if (status === 'success' && riveFile && !riveFileStore.getFile(url)) {
      riveFileStore.setFile(url, riveFile);
    }

    if (status !== 'success' && status !== 'failed') return;

    loggedRef.current = true;
    logRiveLoad({
      src: url,
      durationMs: Math.round(performance.now() - startedAtRef.current),
      activeRiveCount: mountInfoRef.current?.active ?? riveLoadInstrumentation.getActive(),
      mountIndex: mountInfoRef.current?.mountIndex ?? 0,
      cacheHit: false,
      isPreload: true,
      outcome: status === 'success' ? 'success' : 'error',
      errorMessage: status === 'failed' ? 'useRiveFile failed' : undefined,
    });
  }, [file, riveFileStore, url]);

  return null;
});

export default RiveFilePreloader;
